import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo} from "react";
import {personsSelector, useGetAllPersonsQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {PersonDataTable} from "../../components/data-table/PersonDataTable/PersonDataTable";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {useLocation, useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";

export type PersonPageProps = {
  showStatus?: boolean
}

export const PersonDataTablePageRoute = () => {
  const {state} = useLocation();
  const showStatus = state?.showStatus;
  return (
    <PersonDataTablePage
      showStatus={showStatus}
    />
  );
};

export const PersonDataTablePage = (props: PersonPageProps) => {
  const {showStatus} = props;

  const {t} = useTranslation(["translation", "common"]);
  const {regionId} = useApp();
  const navigate = useNavigate()

  const selectPersons = useMemo(() => {
    return personsSelector();
  }, []);

  const {persons, isLoading, refetch,} = useGetAllPersonsQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      persons: selectPersons(result, undefined),
      isLoading: result.isLoading,
    })
  });

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.person.alt.plural", "Users", {ns: "common"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.actions.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["edit"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/persons/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <PersonDataTable
                  withBorder
                  records={persons}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isLoading}
                  showStatus={showStatus}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
